import { memo, useCallback, useMemo, useState } from 'react';

import { BanIcon, PencilIcon, PlusIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CollapsibleSection } from '@/components/atoms/CollapsibleSection/CollapsibleSection';
import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import {
  PopoverComboboxContent,
  PopoverComboboxEmpty,
  PopoverComboboxGroup,
  PopoverComboboxInput,
  PopoverComboboxItem,
  PopoverComboboxList,
} from '@/components/ui/custom/PopoverCombobox';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import useBrains from '@/hooks/useBrains';
import { useDeskId } from '@/hooks/useDeskId';
import { useDepartments } from '@/modules/departments/hooks/useDepartments';
import { AgentIcon } from '@/modules/humanChat/components/AgentIcon/AgentIcon';
import { AssigneePopover } from '@/modules/humanChat/components/AssigneePopover/AssigneePopover';
import {
  useConversationDetail,
  useConversationMutations,
} from '@/modules/humanChat/hooks/useConversations';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAllAgents } from '@/redux/presence/selectors';
import { selectSessionId } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { ContextTagsSection } from '../Sections/ContextTagsSection';
import { FollowUpsSection } from '../Sections/FollowUpsSection';
import { InformationSection } from '../Sections/InformationSection';
import { RelatedConversationsSection } from '../Sections/RelatedConversationsSection';

export const OverviewPanel = memo(() => {
  // Local state
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [assigneePopoverOpen, setAssigneePopoverOpen] = useState(false);
  const [departmentPopoverOpen, setDepartmentPopoverOpen] = useState(false);

  // Redux selectors
  const agents = useSelector(selectAllAgents);
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.WRITE)
  );

  // Custom hooks
  const { t } = useTranslation();
  const { conversation } = useConversationDetail({ deskId, sessionId });
  const { updateConversation } = useConversationMutations({ deskId, sessionId });
  const { departments } = useDepartments(deskId);
  const { brain } = useBrains(conversation?.assignee_brain_id);

  // Memoized variables
  const defaultBrainName = useMemo(() => t('common.virtual_assistant'), [t]);
  const departmentId = conversation?.department_id;
  const activeDepartment = useMemo(
    () => departments?.find((d) => d.department_id === departmentId),
    [departmentId, departments]
  );

  const filteredDepartments = useMemo(() => {
    let filtered = departments || [];

    if (departmentSearchTerm.trim()) {
      filtered =
        departments?.filter((department) =>
          department.name?.toLowerCase().includes(departmentSearchTerm.toLowerCase())
        ) || [];
    }

    // Selected first, on a copy: unfiltered, `filtered` is the `useDepartments` cache array.
    if (filtered.length > 0) {
      return [...filtered].sort((a, b) => {
        const aIsSelected = a.department_id === departmentId;
        const bIsSelected = b.department_id === departmentId;

        if (aIsSelected && !bIsSelected) {
          return -1;
        }
        if (!aIsSelected && bIsSelected) {
          return 1;
        }
        return 0;
      });
    }

    return filtered;
  }, [departments, departmentSearchTerm, departmentId]);

  const activeDepartmentInFilteredIndex = useMemo(() => {
    if (!filteredDepartments || !departmentId) {
      return null;
    }
    const active = filteredDepartments?.findIndex((d) => d.department_id === departmentId);
    return active !== -1 ? active : null;
  }, [filteredDepartments, departmentId]);

  /** Agent the conversation is assigned to, if the assignee is a human. */
  const assignedAgent = useMemo(
    () =>
      conversation?.assignee_agent_id
        ? (agents?.find((agent) => agent.agent_id === conversation.assignee_agent_id) ?? null)
        : null,
    [agents, conversation?.assignee_agent_id]
  );

  /** Name to show when the conversation is assigned to an AI agent instead. */
  const assignedBrainName = useMemo(() => {
    if (assignedAgent || !conversation?.assignee_brain_id) {
      return null;
    }
    return brain?.name || defaultBrainName;
  }, [assignedAgent, conversation?.assignee_brain_id, brain?.name, defaultBrainName]);

  const handleSelectDepartment = useCallback(
    (i: number | null) => {
      const selectedDepartment = i !== null ? filteredDepartments[i] : null;
      updateConversation({
        department_id: selectedDepartment ? selectedDepartment.department_id : null,
      });
      setDepartmentSearchTerm('');
      setDepartmentPopoverOpen(false);
    },
    [filteredDepartments, updateConversation]
  );

  return (
    <>
      <CollapsibleSection
        title={t('conversation.assigned')}
        showBorder={false}
        storageKey="humanChat.sidebar.assigned"
      >
        <div className="flex flex-col gap-1 pb-3">
          <AssigneePopover
            sessionId={sessionId}
            assigneeAgentId={conversation?.assignee_agent_id}
            assigneeBrainId={conversation?.assignee_brain_id}
            open={assigneePopoverOpen}
            onOpenChange={setAssigneePopoverOpen}
            trigger={
              <button
                type="button"
                disabled={!canWrite}
                className="group/assignee flex w-full items-center gap-0.5 rounded-md px-1.5 py-2.5 text-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                {assignedAgent || assignedBrainName ? (
                  <>
                    {assignedBrainName ? (
                      <ZapIcon size={16} className="text-muted-foreground" />
                    ) : (
                      <AgentIcon agent={assignedAgent} />
                    )}
                    <TruncatedText
                      text={assignedBrainName ?? assignedAgent?.name ?? ''}
                      className="min-w-0 flex-1 text-left font-normal text-foreground"
                      align="start"
                    />
                    <PencilIcon
                      size={14}
                      className="ml-1 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/assignee:opacity-100"
                    />
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-left font-normal text-muted-foreground">
                      {t('departments.no_assignee')}
                    </span>
                    <PlusIcon size={16} className="ml-1 shrink-0 text-primary" />
                  </>
                )}
              </button>
            }
          />

          {departments?.length > 0 && (
            <Popover open={departmentPopoverOpen} onOpenChange={setDepartmentPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={!canWrite}
                  className="group/department flex w-full items-center gap-0.5 rounded-md px-1.5 py-2.5 text-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {activeDepartment?.icon}
                  {activeDepartment ? (
                    <>
                      <span className="min-w-0 flex-1 truncate text-left font-normal text-foreground">
                        {activeDepartment?.name}
                      </span>
                      <PencilIcon
                        size={14}
                        className="ml-1 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/department:opacity-100"
                      />
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-left font-normal text-muted-foreground">
                        {t('departments.no_department')}
                      </span>
                      <PlusIcon size={16} className="ml-1 shrink-0 text-primary" />
                    </>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverComboboxContent align="end" className="w-56">
                <PopoverComboboxInput
                  value={departmentSearchTerm}
                  onValueChange={setDepartmentSearchTerm}
                  placeholder="departments.assign_a_department"
                />
                <PopoverComboboxList>
                  {activeDepartment && (
                    <PopoverComboboxGroup>
                      <PopoverComboboxItem
                        value="__unassign__"
                        onSelect={() => handleSelectDepartment(null)}
                      >
                        <BanIcon size={16} className="mr-2" />
                        {t('conversation.unassign')}
                      </PopoverComboboxItem>
                    </PopoverComboboxGroup>
                  )}
                  {filteredDepartments.length > 0 ? (
                    <PopoverComboboxGroup>
                      {filteredDepartments.map((department, index) => (
                        <PopoverComboboxItem
                          key={department.department_id}
                          value={department.department_id}
                          onSelect={() => handleSelectDepartment(index)}
                          selected={activeDepartmentInFilteredIndex === index}
                          className="flex items-center gap-2"
                        >
                          {department?.icon}
                          <TruncatedText text={department.name} />
                        </PopoverComboboxItem>
                      ))}
                    </PopoverComboboxGroup>
                  ) : (
                    departmentSearchTerm.trim() && (
                      <PopoverComboboxEmpty>{t('common.no_results')}</PopoverComboboxEmpty>
                    )
                  )}
                </PopoverComboboxList>
              </PopoverComboboxContent>
            </Popover>
          )}
        </div>
      </CollapsibleSection>

      <InformationSection />

      <ContextTagsSection />

      <RelatedConversationsSection key={sessionId} />

      <FollowUpsSection />
    </>
  );
});

OverviewPanel.displayName = 'OverviewPanel';
